import { z } from "zod";
import { oc } from "@orpc/contract";
import { leaseableResourcePauseSchema } from "@erp/shared-types";

export const SentryTestResponseSchema = z.object({
  captured: z.boolean(),
});

export const sentryTestContract = oc.output(SentryTestResponseSchema).route({
  summary: "Send a test error to error tracking",
  description:
    "QA check: raises an intentional backend error and reports it to the error-tracking service to confirm reporting works.",
  method: "POST",
});

export const TestTimeoutInputSchema = z.object({
  ms: z
    .number()
    .int()
    .min(0)
    .max(15 * 60 * 1000),
});

export const TestTimeoutResponseSchema = z.object({
  waitedMs: z.number(),
});

export const testTimeoutContract = oc
  .input(TestTimeoutInputSchema)
  .output(TestTimeoutResponseSchema)
  .route({
    summary: "Wait for a test timeout duration",
    description:
      "QA check: waits for the requested number of milliseconds and reports how long it waited, to exercise client request timeouts.",
    method: "POST",
  });

export const QaApVarianceProfileSchema = z.enum([
  "mirror",
  "price-plus-3pct",
  "qty-minus-one",
  "extra-freight-line",
]);

export const QaApE2eScenarioIdSchema = z.enum([
  "clean-2way-match",
  "tentative-price-variance",
  "order-not-ready",
  "duplicate-invoice",
  "inbound-email-only",
  "no-po-reference",
  "waiting-approval",
]);

export const ApE2eQaStatusSchema = z.object({
  invoiceCount: z.number().int(),
  emailCount: z.number().int(),
});

export const ApE2eQaScenarioSchema = z.object({
  id: QaApE2eScenarioIdSchema,
  label: z.string(),
  description: z.string(),
  requiresPurchaseOrder: z.boolean(),
  defaultVariance: QaApVarianceProfileSchema,
});

export const ApE2eQaPurchaseOrderSummarySchema = z.object({
  id: z.uuid(),
  externalId: z.string(),
  referenceId: z.string().nullable(),
  status: z.string(),
  supplierId: z.uuid(),
  supplierName: z.string(),
  lineCount: z.number().int(),
  totalAmount: z.string().nullable(),
});

export const ApE2eQaCreateInboundEmailResultSchema = z.object({
  batchId: z.string(),
  inboundEmailId: z.uuid(),
  documentId: z.uuid().nullable(),
});

export const ApE2eQaCreateInvoiceResultSchema = z.object({
  batchId: z.string(),
  apInvoiceId: z.uuid(),
  invoiceNumber: z.string(),
  supplierId: z.uuid(),
  purchaseOrderId: z.uuid().nullable(),
  lineCount: z.number().int(),
  totalAmount: z.string(),
});

export const ApE2eQaResetInvoiceResultSchema = z.object({
  apInvoiceId: z.uuid(),
  status: z.string(),
  matchesCleared: z.number().int(),
});

export const ApE2eQaMatchResultSchema = z.object({
  apInvoiceId: z.uuid(),
  matchMethod: z.string(),
  lowestConfidence: z.number().nullable(),
  invoiceStatus: z.string(),
});

export const ApE2eQaInjectScenarioResultSchema = z.object({
  batchId: z.string(),
  scenarioId: QaApE2eScenarioIdSchema,
  inboundEmailId: z.uuid().nullable(),
  apInvoiceIds: z.array(z.uuid()),
  purchaseOrderId: z.uuid().nullable(),
  matchResults: z.array(ApE2eQaMatchResultSchema),
});

export const ApE2eQaInspectResultSchema = z.object({
  kind: z.enum(["invoice", "email"]),
  batchId: z.string().nullable(),
  scenarioId: z.string().nullable(),
  inboundEmailId: z.uuid().nullable(),
  apInvoice: z
    .object({
      id: z.uuid(),
      invoiceNumber: z.string().nullable(),
      status: z.string(),
      paymentStatus: z.string(),
      supplierId: z.uuid().nullable(),
      referenceIds: z.array(z.string()).nullable(),
      totalAmount: z.string(),
      troubleTypes: z.array(z.string()),
      lineCount: z.number().int(),
      matchCount: z.number().int(),
      sourceId: z.uuid().nullable(),
    })
    .nullable(),
  email: z
    .object({
      id: z.uuid(),
      subject: z.string().nullable(),
      from: z.string().nullable(),
      attachmentCount: z.number().int().nullable(),
    })
    .nullable(),
});

export const ApE2eQaCleanupResultSchema = z.object({
  invoicesDeleted: z.number().int(),
  lineItemsDeleted: z.number().int(),
  emailsDeleted: z.number().int(),
  associationsDeleted: z.number().int(),
});

export const apE2eQaContracts = {
  status: oc.output(ApE2eQaStatusSchema).route({
    summary: "Get AP end-to-end QA fixture counts",
    description:
      "QA tool, non-production only: counts the test supplier invoices and inbound emails created by AP end-to-end QA fixtures.",
    method: "GET",
    path: "/ap-e2e/status",
  }),

  listScenarios: oc.output(z.array(ApE2eQaScenarioSchema)).route({
    summary: "List AP end-to-end QA scenarios",
    description:
      "QA tool: lists injectable accounts-payable test scenarios (clean match, price variance, duplicate invoice, etc.) with whether each needs a purchase order.",
    method: "GET",
    path: "/ap-e2e/scenarios",
  }),

  listPurchaseOrders: oc
    .input(
      z.object({
        search: z.string().optional(),
      }),
    )
    .output(z.array(ApE2eQaPurchaseOrderSummarySchema))
    .route({
      summary: "List purchase orders for AP QA scenarios",
      description:
        "QA tool, non-production only: searches the organization's purchase orders with supplier, line count and total, to pick one for an AP test scenario.",
      method: "GET",
      path: "/ap-e2e/purchase-orders",
    }),

  createInboundEmail: oc
    .input(
      z.object({
        batchId: z.string().optional(),
        scenarioId: z.string().optional(),
        subject: z.string().optional(),
        from: z.string().optional(),
        attachInvoicePdf: z.boolean().optional(),
        invoiceNumberHint: z.string().optional(),
      }),
    )
    .output(ApE2eQaCreateInboundEmailResultSchema)
    .route({
      summary: "Create a test AP inbound email",
      description:
        "QA tool, non-production only: creates a fake inbound supplier email, optionally with a generated invoice PDF attachment, tagged to a QA batch.",
      method: "POST",
      path: "/ap-e2e/inbound-email",
    }),

  createInvoice: oc
    .input(
      z.object({
        batchId: z.string().optional(),
        scenarioId: z.string().optional(),
        purchaseOrderId: z.uuid().optional(),
        supplierId: z.uuid().optional(),
        inboundEmailId: z.uuid().optional(),
        invoiceNumber: z.string().optional(),
        varianceProfile: QaApVarianceProfileSchema.optional(),
        referenceIds: z.array(z.string()).optional(),
        status: z.string().optional(),
        flagOrderNotReady: z.boolean().optional(),
      }),
    )
    .output(ApE2eQaCreateInvoiceResultSchema)
    .route({
      summary: "Create a test supplier invoice",
      description:
        "QA tool, non-production only: creates a fake supplier invoice, optionally copied from a purchase order with a price, quantity or freight variance, tagged to a QA batch.",
      method: "POST",
      path: "/ap-e2e/invoice",
    }),

  resetInvoice: oc
    .input(
      z.object({
        apInvoiceId: z.uuid(),
        status: z.string().optional(),
        clearMatches: z.boolean().optional(),
        clearTrouble: z.boolean().optional(),
        clearApproval: z.boolean().optional(),
      }),
    )
    .output(ApE2eQaResetInvoiceResultSchema)
    .route({
      summary: "Reset a test supplier invoice",
      description:
        "QA tool, non-production only: resets a QA supplier invoice's status and optionally clears its matches, trouble flags and approvals.",
      method: "POST",
      path: "/ap-e2e/invoice/reset",
    }),

  runMatch: oc
    .input(
      z.object({
        apInvoiceId: z.uuid(),
        purchaseOrderId: z.uuid(),
      }),
    )
    .output(ApE2eQaMatchResultSchema)
    .route({
      summary: "Match a test invoice to a purchase order",
      description:
        "QA tool, non-production only: runs real invoice-to-purchase-order line matching on a QA supplier invoice and returns match method, lowest confidence and invoice status.",
      method: "POST",
      path: "/ap-e2e/invoice/match",
    }),

  injectScenario: oc
    .input(
      z.object({
        scenarioId: QaApE2eScenarioIdSchema,
        purchaseOrderId: z.uuid().optional(),
        varianceProfile: QaApVarianceProfileSchema.optional(),
        batchId: z.string().optional(),
      }),
    )
    .output(ApE2eQaInjectScenarioResultSchema)
    .route({
      summary: "Inject an AP end-to-end test scenario",
      description:
        "QA tool, non-production only: creates the email, supplier invoices and matches for a named AP scenario, optionally against a chosen purchase order and variance.",
      method: "POST",
      path: "/ap-e2e/scenarios/inject",
    }),

  inspect: oc
    .input(
      z.union([
        z.object({ apInvoiceId: z.uuid() }),
        z.object({ inboundEmailId: z.uuid() }),
      ]),
    )
    .output(ApE2eQaInspectResultSchema)
    .route({
      summary: "Inspect an AP QA fixture",
      description:
        "QA tool, non-production only: returns status, payment status, references, trouble flags, line and match counts, and email details for a QA supplier invoice or inbound email.",
      method: "POST",
      path: "/ap-e2e/inspect",
    }),

  cleanup: oc
    .input(
      z.object({
        batchId: z.string().optional(),
      }),
    )
    .output(ApE2eQaCleanupResultSchema)
    .route({
      summary: "Delete AP end-to-end QA fixtures",
      description:
        "QA tool, non-production only: deletes QA-created supplier invoices, line items, emails and links, for one batch or all; returns deleted counts.",
      method: "DELETE",
      path: "/ap-e2e/fixtures",
    }),
} as const;

// ---------------------------------------------------------------------------
// Swag Labs HITL — agent task loop + human escalation against a mock store
// ---------------------------------------------------------------------------

export const QaSwagPersonaSchema = z.enum([
  "standard_user",
  "locked_out_user",
  "problem_user",
  "performance_glitch_user",
  "error_user",
  "visual_user",
]);

export const SwagLabsQaSubtaskSummarySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  status: z.string(),
  taskTypeKey: z.string().nullable(),
  assigneeType: z.string().nullable(),
});

export const SwagLabsQaOrderSummarySchema = z.object({
  taskId: z.uuid(),
  title: z.string(),
  status: z.string(),
  username: z.string().nullable(),
  skus: z.array(z.string()),
  hasReceipt: z.boolean(),
  createdAt: z.string(),
  subtasks: z.array(SwagLabsQaSubtaskSummarySchema),
});

export const SwagLabsQaPersonaInfoSchema = z.object({
  username: QaSwagPersonaSchema,
  summary: z.string(),
  expectedOutcome: z.enum(["completed", "needs_input", "failed"]),
});

export const SwagLabsQaProductSchema = z.object({
  sku: z.string(),
  name: z.string(),
  priceUsd: z.number(),
});

/**
 * Which shopper agent the intake workflow hands new orders to: the in-process
 * mock store (fast, deterministic) or the real operator driving saucedemo.com
 * in a Kernel browser.
 */
export const QaSwagShopperModeSchema = z.enum(["mock", "live"]);

/**
 * Which password the seeded Swag Labs credential currently holds. `unknown` is a credential that
 * carries no QA stamp — the vault cannot be read back, so its secret is not knowable from here.
 */
export const QaSwagPasswordStateSchema = z.enum(["working", "broken"]);
export const QaSwagPasswordStateReadingSchema = z.enum([
  "working",
  "broken",
  "unknown",
]);

/**
 * The stored credential the live shopper signs in with (by reference, through the operator's
 * `fill_credential` tool) and the leaseable resource wrapping it. Breaking the password is what
 * makes the operator report `credential_rejected`, which pauses the resource.
 */
export const SwagLabsQaStoredCredentialSchema = z.object({
  credentialId: z.uuid(),
  credentialName: z.string(),
  username: z.string(),
  resourceId: z.uuid().nullable(),
  passwordState: QaSwagPasswordStateReadingSchema,
  resourceActive: z.boolean(),
  /** An operator-reported rejection paused it — the only state Resume may lift. */
  resourcePaused: z.boolean(),
  pause: leaseableResourcePauseSchema.nullable(),
  lastRejectedAt: z.string().nullable(),
  /** The live shopper requires the resource, so a pause stops it from starting at all. */
  liveAgentLinked: z.boolean(),
});

export const SwagLabsQaStatusSchema = z.object({
  /** Static reference data so the panel never duplicates the mock store. */
  personas: z.array(SwagLabsQaPersonaInfoSchema),
  catalog: z.array(SwagLabsQaProductSchema),
  /** The shared core_namespaces row all fixtures belong to. */
  namespaceId: z.uuid().nullable(),
  taskTypes: z.array(z.object({ key: z.string(), id: z.uuid().nullable() })),
  taskPageId: z.uuid().nullable(),
  agentDefinitionId: z.uuid().nullable(),
  liveAgentDefinitionId: z.uuid().nullable(),
  shopperMode: QaSwagShopperModeSchema,
  /** KERNEL_API_KEY + AI_SERVICE_URL are configured on this backend. */
  liveBrowserReady: z.boolean(),
  intakeWorkflowId: z.uuid().nullable(),
  kickWorkflowId: z.uuid().nullable(),
  seeded: z.boolean(),
  orderCount: z.number().int(),
  agentQueue: z.object({
    pending: z.number().int(),
    inProgress: z.number().int(),
    needsInput: z.number().int(),
  }),
  recentOrders: z.array(SwagLabsQaOrderSummarySchema),
  storedCredential: SwagLabsQaStoredCredentialSchema.nullable(),
});

export const SwagLabsQaSeedResultSchema = z.object({
  namespaceId: z.uuid(),
  namespaceCreated: z.boolean(),
  taskTypesCreated: z.number().int(),
  taskPageCreated: z.boolean(),
  agentDefinitionId: z.uuid(),
  agentDefinitionCreated: z.boolean(),
  liveAgentDefinitionId: z.uuid(),
  liveAgentDefinitionCreated: z.boolean(),
  shopperMode: QaSwagShopperModeSchema,
  intakeWorkflowId: z.uuid(),
  kickWorkflowId: z.uuid(),
  credentialId: z.uuid(),
  credentialCreated: z.boolean(),
  resourceId: z.uuid(),
  resourceCreated: z.boolean(),
});

export const SwagLabsQaShopperModeResultSchema = z.object({
  shopperMode: QaSwagShopperModeSchema,
  intakeWorkflowId: z.uuid(),
});

export const SwagLabsQaPlaceOrderInputSchema = z.object({
  username: QaSwagPersonaSchema,
  skus: z.array(z.string().min(1)).min(1).max(6),
  notes: z.string().max(2000).optional(),
  priority: z.enum(["low", "medium", "high", "urgent"]).optional(),
});

export const SwagLabsQaPlaceOrderResultSchema = z.object({
  workflowRunId: z.uuid(),
  intakeWorkflowId: z.uuid(),
});

export const SwagLabsQaKickResultSchema = z.object({
  workflowRunId: z.uuid(),
  kickWorkflowId: z.uuid(),
});

export const SwagLabsQaCleanupResultSchema = z.object({
  namespaceArchived: z.boolean(),
  tasksDeleted: z.number().int(),
  taskPageDeleted: z.boolean(),
  taskTypesDeleted: z.number().int(),
  workflowsDeleted: z.number().int(),
  agentArchived: z.boolean(),
  resourceDeleted: z.boolean(),
  credentialDeleted: z.boolean(),
  warnings: z.array(z.string()),
});

export const SwagLabsQaStoredPasswordResultSchema = z.object({
  credentialId: z.uuid(),
  passwordState: QaSwagPasswordStateSchema,
});

export const SwagLabsQaResumeResourceResultSchema = z.object({
  resourceId: z.uuid(),
  isActive: z.boolean(),
  resourcePaused: z.boolean(),
});

export const swagLabsHitlQaContracts = {
  status: oc.output(SwagLabsQaStatusSchema).route({
    summary: "Get shopper agent QA status",
    description:
      "QA tool, non-production only: reports the mock-store shopper agent test setup, seeded workflows, shopper mode, agent queue, recent orders and stored credential state.",
    method: "GET",
    path: "/swag-labs-hitl/status",
  }),

  seed: oc.output(SwagLabsQaSeedResultSchema).route({
    summary: "Seed shopper agent QA fixtures",
    description:
      "QA tool, non-production only: creates or refreshes the task types, task page, shopper agents, order workflows and stored credential for the shopper-agent test loop.",
    method: "POST",
    path: "/swag-labs-hitl/seed",
  }),

  placeOrder: oc
    .input(SwagLabsQaPlaceOrderInputSchema)
    .output(SwagLabsQaPlaceOrderResultSchema)
    .route({
      summary: "Place a test shopper order",
      description:
        "QA tool, non-production only: starts the intake workflow with a test persona, up to six product SKUs, notes and priority; returns the workflow run.",
      method: "POST",
      path: "/swag-labs-hitl/orders",
    }),

  kickShopper: oc.output(SwagLabsQaKickResultSchema).route({
    summary: "Start the test shopper agent",
    description:
      "QA tool, non-production only: starts the workflow that hands pending test orders to the shopper agent; returns the workflow run.",
    method: "POST",
    path: "/swag-labs-hitl/kick",
  }),

  setShopperMode: oc
    .input(z.object({ mode: QaSwagShopperModeSchema }))
    .output(SwagLabsQaShopperModeResultSchema)
    .route({
      summary: "Switch the test shopper mode",
      description:
        "QA tool, non-production only: switches test orders between the in-process mock store and a live browser shopper agent by re-syncing the intake workflow.",
      method: "PUT",
      path: "/swag-labs-hitl/shopper-mode",
    }),

  setStoredPassword: oc
    .input(z.object({ state: QaSwagPasswordStateSchema }))
    .output(SwagLabsQaStoredPasswordResultSchema)
    .route({
      summary: "Break or fix the test shopper password",
      description:
        "QA tool, non-production only: sets the stored test shopper credential to a working or deliberately broken password, to exercise credential-rejection handling.",
      method: "PUT",
      path: "/swag-labs-hitl/stored-password",
    }),

  resumeResource: oc.output(SwagLabsQaResumeResourceResultSchema).route({
    summary: "Resume the paused test shopper credential",
    description:
      "QA tool, non-production only: lifts the pause placed on the test shopper credential after a reported sign-in rejection.",
    method: "POST",
    path: "/swag-labs-hitl/resume-resource",
  }),

  cleanup: oc.output(SwagLabsQaCleanupResultSchema).route({
    summary: "Delete shopper agent QA fixtures",
    description:
      "QA tool, non-production only: deletes the shopper-agent test tasks, task types, task page, workflows, credential and resource and archives its agent.",
    method: "DELETE",
    path: "/swag-labs-hitl/fixtures",
  }),
} as const;

export const KernelTelemetryQaStatusSchema = z.object({
  seeded: z.boolean(),
  agentDefinitionId: z.uuid().nullable(),
  agentRunId: z.uuid().nullable(),
  documentId: z.uuid().nullable(),
  eventCount: z.number().int(),
  notableCount: z.number().int(),
});

export const KernelTelemetryQaSeedResultSchema =
  KernelTelemetryQaStatusSchema.extend({
    agentDefinitionCreated: z.boolean(),
    agentRunCreated: z.boolean(),
    telemetryCreated: z.boolean(),
  });

export const KernelTelemetryQaCleanupResultSchema = z.object({
  documentsDeleted: z.number().int(),
  runsDeleted: z.number().int(),
  agentArchived: z.boolean(),
});

export const kernelTelemetryQaContracts = {
  status: oc.output(KernelTelemetryQaStatusSchema).route({
    summary: "Get browser telemetry QA status",
    description:
      "QA tool, non-production only: reports whether the browser-agent telemetry fixture is seeded, with its agent, run, document and event counts.",
    method: "GET",
    path: "/kernel-telemetry/status",
  }),
  seedFromScratch: oc.output(KernelTelemetryQaSeedResultSchema).route({
    summary: "Seed browser telemetry QA fixtures",
    description:
      "QA tool, non-production only: creates a test agent, agent run and recorded browser-session telemetry document for viewing telemetry in the UI.",
    method: "POST",
    path: "/kernel-telemetry/seed",
  }),
  cleanup: oc.output(KernelTelemetryQaCleanupResultSchema).route({
    summary: "Delete browser telemetry QA fixtures",
    description:
      "QA tool, non-production only: deletes the browser telemetry test documents and agent runs and archives the test agent.",
    method: "DELETE",
    path: "/kernel-telemetry/fixtures",
  }),
} as const;

export const TaskOutcomesQaStatusSchema = z.object({
  types: z.array(z.object({ id: z.uuid(), name: z.string() })),
  tasks: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      status: z.string(),
      pinned: z.boolean(),
    }),
  ),
});

export const taskOutcomesQaContracts = {
  status: oc.output(TaskOutcomesQaStatusSchema).route({
    summary: "Get task outcome QA fixtures",
    description:
      "QA tool, non-production only: lists the test task types and tasks seeded for task outcome testing, with status and whether an outcome contract is pinned.",
    method: "GET",
    path: "/task-outcomes/status",
  }),
  seed: oc.output(TaskOutcomesQaStatusSchema).route({
    summary: "Seed task outcome QA fixtures",
    description:
      "QA tool, non-production only: creates test task types with an outcome vocabulary and tasks in pending, in-progress and completed states, without starting workflows or agents.",
    method: "POST",
    path: "/task-outcomes/seed",
  }),
  cleanup: oc.output(z.object({ tasksDeleted: z.number().int() })).route({
    summary: "Delete task outcome QA fixtures",
    description:
      "QA tool, non-production only: deletes the test tasks, task types and outcome vocabulary created for task outcome testing.",
    method: "DELETE",
    path: "/task-outcomes/fixtures",
  }),
};

export const qaContracts = {
  taskOutcomes: taskOutcomesQaContracts,
  sentryTest: sentryTestContract,
  testTimeout: testTimeoutContract,
  apE2e: apE2eQaContracts,
  swagLabsHitl: swagLabsHitlQaContracts,
  kernelTelemetry: kernelTelemetryQaContracts,
};

export type SentryTestResponse = z.infer<typeof SentryTestResponseSchema>;
export type TestTimeoutInput = z.infer<typeof TestTimeoutInputSchema>;
export type TestTimeoutResponse = z.infer<typeof TestTimeoutResponseSchema>;
export type QaApVarianceProfile = z.infer<typeof QaApVarianceProfileSchema>;
export type QaApE2eScenarioId = z.infer<typeof QaApE2eScenarioIdSchema>;
export type ApE2eQaScenario = z.infer<typeof ApE2eQaScenarioSchema>;
export type ApE2eQaPurchaseOrderSummary = z.infer<
  typeof ApE2eQaPurchaseOrderSummarySchema
>;
export type ApE2eQaStatus = z.infer<typeof ApE2eQaStatusSchema>;
export type ApE2eQaInjectScenarioResult = z.infer<
  typeof ApE2eQaInjectScenarioResultSchema
>;
export type ApE2eQaInspectResult = z.infer<typeof ApE2eQaInspectResultSchema>;
export type QaSwagPersona = z.infer<typeof QaSwagPersonaSchema>;
export type QaSwagShopperMode = z.infer<typeof QaSwagShopperModeSchema>;
export type SwagLabsQaStatus = z.infer<typeof SwagLabsQaStatusSchema>;
export type SwagLabsQaOrderSummary = z.infer<
  typeof SwagLabsQaOrderSummarySchema
>;
export type SwagLabsQaSeedResult = z.infer<typeof SwagLabsQaSeedResultSchema>;
export type SwagLabsQaPlaceOrderInput = z.infer<
  typeof SwagLabsQaPlaceOrderInputSchema
>;
export type SwagLabsQaCleanupResult = z.infer<
  typeof SwagLabsQaCleanupResultSchema
>;
export type KernelTelemetryQaStatus = z.infer<
  typeof KernelTelemetryQaStatusSchema
>;
export type KernelTelemetryQaSeedResult = z.infer<
  typeof KernelTelemetryQaSeedResultSchema
>;
export type KernelTelemetryQaCleanupResult = z.infer<
  typeof KernelTelemetryQaCleanupResultSchema
>;
export type QaSwagPasswordState = z.infer<typeof QaSwagPasswordStateSchema>;
export type SwagLabsQaStoredCredential = z.infer<
  typeof SwagLabsQaStoredCredentialSchema
>;
